import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Building2,
  Users,
  Package,
  FolderTree,
  Truck,
  ArrowRight,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import { supabase } from "@/integrations/supabase/client";

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatActionLabel = (action: string): string =>
  action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatEntityType = (entityType: string): string =>
  entityType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ── Sub-components ────────────────────────────────────────────────────────────

type Accent = "white" | "green" | "amber" | "red" | "muted";

const accentClass: Record<Accent, string> = {
  white: "text-white",
  green: "text-emerald-400",
  amber: "text-amber-400",
  red: "text-red-400",
  muted: "text-slate-400",
};

interface MetricRowProps {
  label: string;
  value: number | undefined;
  loading: boolean;
  accent?: Accent;
  to?: string;
}

const MetricRow = ({ label, value, loading, accent = "white", to }: MetricRowProps) => {
  const navigate = useNavigate();
  return (
    <div
      role={to ? "button" : undefined}
      tabIndex={to ? 0 : undefined}
      onClick={to ? () => navigate(to) : undefined}
      onKeyDown={
        to
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(to);
              }
            }
          : undefined
      }
      className={`flex items-center justify-between py-2 ${
        to ? "cursor-pointer hover:bg-slate-800/40 -mx-3 px-3 rounded" : ""
      }`}
    >
      <span className="text-sm text-slate-300">{label}</span>
      {loading ? (
        <div className="h-5 w-10 rounded bg-slate-700 animate-pulse" />
      ) : (
        <span className={`text-lg font-semibold tabular-nums ${accentClass[accent]}`}>
          {value ?? 0}
        </span>
      )}
    </div>
  );
};

type BadgeTone = "amber" | "red" | "green";

const badgeToneClass: Record<BadgeTone, string> = {
  amber: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  red: "bg-red-500/10 text-red-400 border-red-500/30",
  green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

interface DashCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  to?: string;
  badge?: { label: string; tone: BadgeTone };
}

const DashCard = ({ title, icon, children, to, badge }: DashCardProps) => {
  const navigate = useNavigate();
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-md p-4 flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-slate-300">
          {icon}
          <h3 className="text-sm font-semibold uppercase tracking-wide">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {badge && (
            <span
              className={`px-2 py-0.5 rounded text-[10px] font-medium border ${badgeToneClass[badge.tone]}`}
            >
              {badge.label}
            </span>
          )}
          {to && (
            <button
              type="button"
              onClick={() => navigate(to)}
              className="text-slate-500 hover:text-slate-300 transition-colors"
              aria-label={`Open ${title}`}
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="divide-y divide-slate-700/60 flex-1">{children}</div>
    </div>
  );
};

// ── Attention Required Banner ─────────────────────────────────────────────────

interface AttentionBannerProps {
  metrics: ReturnType<typeof useDashboardMetrics>["metrics"];
  loading: boolean;
}

const AttentionBanner = ({ metrics, loading }: AttentionBannerProps) => {
  const navigate = useNavigate();
  const items = [
    metrics?.pendingRequests && metrics.pendingRequests > 0
      ? {
          label: `${metrics.pendingRequests} pending request${metrics.pendingRequests > 1 ? "s" : ""}`,
          tone: "amber" as const,
          to: "/requests",
        }
      : null,
    metrics?.noAdminCompanies && metrics.noAdminCompanies > 0
      ? {
          label: `${metrics.noAdminCompanies} ${
            metrics.noAdminCompanies > 1 ? "companies" : "company"
          } without admin`,
          tone: "red" as const,
          to: "/companies?hasNoAdmin=true",
        }
      : null,
    metrics?.stale180PlusItems && metrics.stale180PlusItems > 0
      ? {
          label: `${metrics.stale180PlusItems} item(s) overdue (${metrics.staleRedDays ?? 180}+ days)`,
          tone: "red" as const,
          to: "/catalog/system-items?tab=overdue",
        }
      : null,
    metrics?.stale90180Items && metrics.stale90180Items > 0
      ? {
          label: `${metrics.stale90180Items} item(s) approaching stale (${metrics.staleAmberDays ?? 90}–${metrics.staleRedDays ?? 180} days)`,
          tone: "amber" as const,
          to: "/catalog/system-items?tab=stale",
        }
      : null,
  ].filter(Boolean) as { label: string; tone: "amber" | "red"; to: string }[];

  if (loading) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-md p-4">
        <div className="h-4 w-32 rounded bg-slate-700 animate-pulse mb-3" />
        <div className="flex flex-wrap gap-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-7 w-40 rounded bg-slate-700 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-md p-4 flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
        <div>
          <p className="text-sm font-medium text-white">All clear</p>
          <p className="text-xs text-slate-400">No items require your attention right now.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-md p-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-amber-400" />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
          Attention Required
        </h2>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((item, i) => (
          <button
            key={i}
            type="button"
            onClick={() => navigate(item.to)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border transition-colors ${
              item.tone === "red"
                ? "bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20"
                : "bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20"
            }`}
          >
            {item.tone === "red" ? (
              <AlertTriangle className="h-3.5 w-3.5" />
            ) : (
              <Clock className="h-3.5 w-3.5" />
            )}
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Main Dashboard ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { metrics, isLoading, error, lastUpdated, refetch, isFetching } = useDashboardMetrics();

  useEffect(() => {
    const channel = supabase
      .channel("admin-dashboard-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "companies" }, () => refetch())
      .on("postgres_changes", { event: "*", schema: "public", table: "profiles" }, () => refetch())
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "company_suspension_requests" },
        () => refetch(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "system_line_items" },
        () => refetch(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "admin_audit_logs" },
        () => refetch(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const staleBadge = metrics
    ? metrics.stale180PlusItems > 0
      ? { label: `${metrics.stale180PlusItems} overdue`, tone: "red" as const }
      : metrics.stale90180Items > 0
        ? { label: `${metrics.stale90180Items} stale`, tone: "amber" as const }
        : { label: "All current", tone: "green" as const }
    : undefined;

  const alertsBadge = metrics
    ? metrics.pendingRequests > 0 || metrics.noAdminCompanies > 0
      ? {
          label: `${metrics.pendingRequests + metrics.noAdminCompanies} issue${
            metrics.pendingRequests + metrics.noAdminCompanies > 1 ? "s" : ""
          }`,
          tone: "red" as const,
        }
      : { label: "All clear", tone: "green" as const }
    : undefined;

  return (
    <div className="space-y-6 max-w-7xl">
      {/* Attention Required */}
      <AttentionBanner metrics={metrics} loading={isLoading} />

      {/* Main grid — 3 columns on large screens */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Companies */}
        <DashCard
          title="Companies"
          icon={<Building2 className="h-4 w-4" />}
          to="/companies"
        >
          <MetricRow label="Total" value={metrics?.totalCompanies} loading={isLoading} to="/companies" />
          <MetricRow label="Active" value={metrics?.activeCompanies} loading={isLoading} accent="green" to="/companies?status=active" />
          <MetricRow label="Suspended" value={metrics?.suspendedCompanies} loading={isLoading} accent="amber" to="/companies?status=suspended" />
        </DashCard>

        {/* Users */}
        <DashCard title="Users" icon={<Users className="h-4 w-4" />} to="/users">
          <MetricRow label="Total" value={metrics?.totalUsers} loading={isLoading} to="/users" />
          <MetricRow label="Active" value={metrics?.activeUsers} loading={isLoading} accent="green" to="/users?status=active" />
          <MetricRow label="Suspended" value={metrics?.suspendedUsers} loading={isLoading} accent="amber" to="/users?status=suspended" />
          <MetricRow label="Deactivated" value={metrics?.deactivatedUsers} loading={isLoading} accent="muted" to="/users?status=deactivated" />
        </DashCard>

        {/* System Items */}
        <DashCard
          title="System Items"
          icon={<Package className="h-4 w-4" />}
          to="/catalog/system-items"
          badge={staleBadge}
        >
          <MetricRow label="Active" value={metrics?.activeSystemItems} loading={isLoading} accent="green" to="/catalog/system-items?tab=active" />
          <MetricRow label="Inactive" value={metrics?.inactiveSystemItems} loading={isLoading} accent="muted" to="/catalog/system-items?tab=inactive" />
          <MetricRow label={`Stale (${metrics?.staleAmberDays ?? 90}–${metrics?.staleRedDays ?? 180}d)`} value={metrics?.stale90180Items} loading={isLoading} accent="amber" to="/catalog/system-items?tab=stale" />
          <MetricRow label={`Overdue (${metrics?.staleRedDays ?? 180}+d)`} value={metrics?.stale180PlusItems} loading={isLoading} accent="red" to="/catalog/system-items?tab=overdue" />
        </DashCard>

        {/* Catalog */}
        <DashCard
          title="Catalog"
          icon={<FolderTree className="h-4 w-4" />}
          to="/catalog/categories"
        >
          <MetricRow label="Browse Categories" value={metrics?.totalBrowseCategories} loading={isLoading} to="/catalog/categories" />
          <MetricRow label="Platform Suppliers" value={metrics?.totalPlatformSuppliers} loading={isLoading} to="/catalog/suppliers" />
        </DashCard>

        {/* Alerts */}
        <DashCard title="Alerts" icon={<AlertTriangle className="h-4 w-4" />} badge={alertsBadge}>
          <MetricRow
            label="Pending Requests"
            value={metrics?.pendingRequests}
            loading={isLoading}
            accent={metrics && metrics.pendingRequests > 0 ? "amber" : "green"}
            to="/requests"
          />
          <MetricRow
            label="No-Admin Companies"
            value={metrics?.noAdminCompanies}
            loading={isLoading}
            accent={metrics && metrics.noAdminCompanies > 0 ? "red" : "green"}
            to="/companies?hasNoAdmin=true"
          />
        </DashCard>
      </div>

      {/* Recent Activity — full width */}
      <div className="bg-slate-800 border border-slate-700 rounded-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-slate-300">
            <Truck className="h-4 w-4" />
            <h3 className="text-sm font-semibold uppercase tracking-wide">Recent Activity</h3>
          </div>
          <button
            type="button"
            onClick={() => (window.location.href = "/audit")}
            className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1"
          >
            View all <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        <div className="divide-y divide-slate-700/60">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between py-2">
                <div className="space-y-1.5">
                  <div className="h-4 w-40 rounded bg-slate-700 animate-pulse" />
                  <div className="h-3 w-56 rounded bg-slate-700/70 animate-pulse" />
                </div>
                <div className="h-3 w-20 rounded bg-slate-700 animate-pulse" />
              </div>
            ))
          ) : !metrics?.recentAudit || metrics.recentAudit.length === 0 ? (
            <p className="py-4 text-sm text-slate-500">No activity recorded yet.</p>
          ) : (
            metrics.recentAudit.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between py-2.5 gap-4">
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{formatActionLabel(entry.action)}</p>
                  <p className="text-xs text-slate-400 truncate">
                    {formatEntityType(entry.entity_type)} · {entry.admin_name}
                  </p>
                </div>
                <span className="text-xs text-slate-500 shrink-0">
                  {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span>
          {error
            ? "Failed to load metrics."
            : lastUpdated
              ? `Last updated: ${lastUpdated.toLocaleTimeString()}`
              : "Loading..."}
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center justify-center h-6 w-6 rounded hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
          aria-label="Refresh metrics"
          title="Refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>
    </div>
  );
}
